import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, photoUrl, errDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, PencilSimple, Trash, XCircle, Lightning, UploadSimple, X, ChartBar, ArrowSquareOut, Calculator } from "@phosphor-icons/react";
import { toast } from "sonner";
import TradeChart from "../components/TradeChart";
import TradeFormDialog from "../components/TradeFormDialog";
import PhotoCropper from "../components/PhotoCropper";
import PhotoLightbox from "../components/PhotoLightbox";
import SymbolCheck from "../components/SymbolCheck";
import { fmtNum, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const statusColor = {
  open: "border-[hsl(var(--primary))] text-[hsl(var(--primary))]",
  pending: "border-yellow-500 text-yellow-500",
  closed: "border-border text-muted-foreground",
  cancelled: "border-border text-muted-foreground line-through",
};

export default function TradeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { config } = useAuth();
  const [trade, setTrade] = useState(null);
  const [lastPrice, setLastPrice] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [exitPrice, setExitPrice] = useState("");
  const [pushOpen, setPushOpen] = useState(false);
  const [pushConfirm, setPushConfirm] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [exchanges, setExchanges] = useState([]);
  const [sellOnExchange, setSellOnExchange] = useState(false);
  const [sellConfirm, setSellConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trades/${id}`);
      setTrade(data);
    } catch {
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    api.get("/exchanges").then((r) => setExchanges(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (closeOpen) { setSellOnExchange(false); setSellConfirm(false); }
  }, [closeOpen]);

  if (!trade) return <div className="text-muted-foreground p-8">Loading…</div>;

  const fmt = (n) => fmtNum(n, 6);
  const liveEx = exchanges.find((e) => e.trading_mode === "live");
  const EX_LABELS = { bybit: "Bybit", binance: "Binance", okx: "OKX" };
  const exLabel = liveEx ? EX_LABELS[liveEx.exchange] || liveEx.exchange : "";
  const canPush = !!liveEx && trade.status === "open";
  const checkEx = liveEx?.exchange || exchanges[0]?.exchange;
  const marketType = trade.market_type || "futures";
  const sellEx = exchanges.find((e) => e.exchange === trade.source && e.trading_mode === "live");
  const canSellOnClose = marketType === "spot" && !!sellEx;
  const canCalcPnl = trade.status === "open" || trade.status === "pending";
  const unrealized = canCalcPnl && lastPrice != null
    ? (trade.side === "long" ? 1 : -1) * (lastPrice - trade.entry_price) * trade.quantity
    : null;

  const calculatePnl = async () => {
    setCalculating(true);
    try {
      const { data } = await api.get("/market/prices", { params: { symbols: trade.symbol } });
      setLastPrice(data[trade.symbol]);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setCalculating(false);
    }
  };

  const closeTrade = async () => {
    if (sellOnExchange && !sellConfirm) {
      toast.error("Confirm the live sell order, or turn off 'Also sell on exchange'");
      return;
    }
    try {
      const { data } = await api.post(`/trades/${id}/close`, {
        exit_price: parseFloat(exitPrice),
        sell_on_exchange: sellOnExchange,
        confirm: sellOnExchange,
      });
      toast.success(sellOnExchange && data.sell_order_id
        ? `Trade closed — sell order placed on ${EX_LABELS[sellEx.exchange]} (ID ${data.sell_order_id})`
        : "Trade closed");
      setCloseOpen(false);
      load();
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const cancelTrade = async () => {
    await api.put(`/trades/${id}`, { status: "cancelled" });
    toast.success("Trade cancelled");
    load();
  };

  const deleteTrade = async () => {
    await api.delete(`/trades/${id}`);
    toast.success("Trade deleted");
    navigate("/");
  };

  const selectPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const maxMb = config?.max_photo_mb || 10;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`${file.name} exceeds the ${maxMb}MB limit`);
      return;
    }
    setCropFile(file);
  };

  const handleCropDone = async (result) => {
    setCropFile(null);
    if (!result) return;
    const fd = new FormData();
    fd.append("file", result);
    try {
      await api.post(`/trades/${id}/photos`, fd);
      toast.success("Photo added");
      load();
    } catch (err) {
      toast.error(errDetail(err));
    }
  };

  const removePhoto = async (pid) => {
    await api.delete(`/photos/${pid}`);
    load();
  };

  const pushToExchange = async () => {
    setPushing(true);
    const firstTp = trade.take_profits?.[0]?.target_price ?? null;
    try {
      const { data } = await api.post(`/exchanges/${liveEx.exchange}/place-order`, {
        symbol: trade.symbol,
        side: trade.side === "long" ? "Buy" : "Sell",
        order_type: "Limit",
        qty: trade.quantity,
        price: trade.entry_price,
        take_profit: firstTp,
        stop_loss: trade.stop_loss,
        market_type: marketType,
        confirm: true,
        trade_id: trade.id,
      });
      toast.success(`Order placed on ${exLabel} — ID ${data.order_id}`);
      setPushOpen(false);
      load();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setPushing(false);
      setPushConfirm(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="trade-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button data-testid="back-btn" onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors duration-150">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading font-bold text-3xl tracking-tight font-mono">{trade.symbol}</h1>
          <Badge variant="outline" className={`label-caps ${trade.side === "long" ? "border-[hsl(var(--profit))] text-profit" : "border-[hsl(var(--loss))] text-loss"}`}>
            {trade.side}
          </Badge>
          <Badge variant="outline" className="label-caps" data-testid="market-type-badge">
            {marketType === "spot" ? "Spot" : "Futures"}
          </Badge>
          <Badge variant="outline" className={`label-caps ${statusColor[trade.status]}`}>{trade.status}</Badge>
          {trade.exchange_order_id && (
            <Badge variant="outline" className="label-caps border-[hsl(var(--profit))] text-profit" data-testid="sent-to-exchange-badge">
              <Lightning size={12} className="mr-1" weight="fill" />
              {trade.external_id
                ? `Imported from ${EX_LABELS[trade.source] || trade.source}`
                : `Sent to ${EX_LABELS[trade.source] || trade.source}`}
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="open-in-tradingview-btn" variant="outline" size="sm"
            onClick={() => {
              const ex = ["bybit", "binance", "okx"].includes(trade.source) ? trade.source.toUpperCase() : "BYBIT";
              window.open(`https://www.tradingview.com/chart/?symbol=${ex}:${trade.symbol.replace("/", "")}`, "_blank", "noopener");
            }}>
            <ArrowSquareOut size={14} className="mr-1" /> TradingView
          </Button>
          <Button data-testid="open-in-charts-btn" variant="outline" size="sm"
            onClick={() => navigate(`/charts?symbol=${encodeURIComponent(trade.symbol)}`)}>
            <ChartBar size={14} className="mr-1" /> Open in Charts
          </Button>
          {canCalcPnl && (
            <Button data-testid="calc-pnl-btn" variant="outline" size="sm" onClick={calculatePnl} disabled={calculating}>
              <Calculator size={14} className="mr-1" /> {calculating ? "Calculating…" : "Calculate P/L"}
            </Button>
          )}
          {trade.status === "open" && (
            <>
              <Button data-testid="close-trade-btn" variant="outline" size="sm" onClick={() => setCloseOpen(true)}>Close Trade</Button>
              <Button data-testid="cancel-trade-btn" variant="outline" size="sm" onClick={cancelTrade}>
                <XCircle size={14} className="mr-1" /> Cancel
              </Button>
              {canPush && (
                <Button data-testid="push-order-btn" size="sm" variant={trade.exchange_order_id ? "outline" : undefined} onClick={() => setPushOpen(true)}
                  className={trade.exchange_order_id ? "" : "bg-[hsl(var(--profit))] text-white hover:bg-[hsl(var(--profit))] hover:brightness-90 hover:text-white"}>
                  <Lightning size={14} className="mr-1" weight="fill" /> {trade.exchange_order_id ? "Push Again" : `Push to ${exLabel}`}
                </Button>
              )}
            </>
          )}
          <Button data-testid="edit-trade-btn" variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-trade-btn" variant="outline" size="sm" className="text-loss hover:text-loss">
                <Trash size={14} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this trade?</AlertDialogTitle>
                <AlertDialogDescription>This removes the trade and its photos from your journal.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-confirm-btn" onClick={deleteTrade}
                  className="bg-[hsl(var(--loss))] text-white hover:bg-[hsl(var(--loss))] hover:brightness-90 hover:text-white">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <TradeChart trade={trade} />
          <div className="border border-border bg-card p-5">
            <div className="label-caps text-muted-foreground mb-2">Reason / Rationale</div>
            {trade.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trade-tags">
                {trade.tags.map((t) => (
                  <span key={t} className="text-xs font-mono uppercase border border-[hsl(var(--primary))] text-primary px-2 py-0.5">{t}</span>
                ))}
              </div>
            )}
            <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="trade-reason-text">
              {trade.reason || <span className="text-muted-foreground">No reason recorded.</span>}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-border bg-card divide-y divide-border" data-testid="trade-info-panel">
            {[
              ["Date", fmtDateTime(trade.trade_date || trade.created_at), "text-muted-foreground"],
              ["Entry", fmt(trade.entry_price), "text-[hsl(var(--primary))]"],
              ["Quantity", fmt(trade.quantity), ""],
              ["Stop Loss", trade.stop_loss != null ? fmt(trade.stop_loss) : "—", "text-loss"],
              ["Exit", trade.exit_price != null ? fmt(trade.exit_price) : "—", ""],
              ...(canCalcPnl
                ? [["Last Price", lastPrice != null ? fmt(lastPrice) : "—", ""],
                   [trade.status === "pending" ? "Potential P/L" : "Unrealized P/L",
                    unrealized == null ? "—" : `≈ ${unrealized >= 0 ? "+" : ""}${fmt(unrealized.toFixed(2))}`,
                    unrealized == null ? "text-muted-foreground" : unrealized >= 0 ? "text-profit" : "text-loss"]]
                : []),
              ["P/L", trade.pnl == null ? "—" : `${trade.pnl >= 0 ? "+" : ""}${fmt(trade.pnl)}`, trade.pnl == null ? "" : trade.pnl >= 0 ? "text-profit" : "text-loss"],
              ["Source", trade.source, "text-muted-foreground"],
              ["Exchange Order", trade.exchange_order_id || (trade.external_id ? "Imported (no order ID)" : "Not sent"),
               trade.exchange_order_id ? "text-profit" : "text-muted-foreground"],
            ].map(([k, v, cls]) => (
              <div key={k} className="flex justify-between px-4 py-2.5" data-testid={k === "Exchange Order" ? "exchange-order-row" : undefined}>
                <span className="label-caps text-muted-foreground">{k}</span>
                <span className={`font-mono text-sm ${cls}`}>{v}</span>
              </div>
            ))}
          </div>

          {checkEx && (
            <div className="border border-border bg-card p-4" data-testid="symbol-check-block">
              <div className="label-caps text-muted-foreground mb-3">Exchange Symbol Check</div>
              <SymbolCheck exchange={checkEx} symbol={trade.symbol} marketType={marketType} />
            </div>
          )}

          <div className="border border-border bg-card p-4">
            <div className="label-caps text-muted-foreground mb-3">Take Profits</div>
            {trade.take_profits?.length ? (
              <div className="space-y-2">
                {trade.take_profits.map((tp, i) => (
                  <div key={tp.id || i} className="flex justify-between items-center text-sm border border-border px-3 py-2" data-testid={`tp-detail-${i}`}>
                    <span className="font-mono text-profit">TP{i + 1} → {fmt(tp.target_price)}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {tp.trigger_type === "percent" ? `${tp.value}% move` : "price"}
                      {tp.close_size_pct != null ? ` · close ${tp.close_size_pct}%` : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No take profits set.</p>
            )}
          </div>

          <div className="border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps text-muted-foreground">Photos</div>
              <label className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150">
                <UploadSimple size={14} /> Add
                <input data-testid="detail-photo-input" type="file" accept="image/*" className="hidden" onChange={selectPhoto} />
              </label>
            </div>
            {trade.photos?.length ? (
              <div className="grid grid-cols-2 gap-2">
                {trade.photos.map((pid, i) => (
                  <div key={pid} className="relative group border border-border" data-testid={`photo-${pid}`}>
                    <button onClick={() => setLightboxIndex(i)} className="block w-full" data-testid={`photo-open-${pid}`}>
                      <img src={photoUrl(pid)} alt="trade attachment" className="w-full h-24 object-cover" />
                    </button>
                    <button data-testid={`photo-delete-${pid}`} onClick={() => removePhoto(pid)}
                      className="absolute top-1 right-1 bg-black/70 p-0.5 opacity-0 group-hover:opacity-100 text-white hover:text-[hsl(var(--loss))] transition-colors duration-150">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No photos attached.</p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-sm bg-card border-border" data-testid="close-trade-dialog">
          <DialogHeader><DialogTitle className="font-heading">Close Trade</DialogTitle></DialogHeader>
          <Label className="label-caps text-muted-foreground">Exit Price</Label>
          <Input data-testid="exit-price-input" type="number" step="any" value={exitPrice}
            onChange={(e) => setExitPrice(e.target.value)} className="font-mono" />
          {canSellOnClose && (
            <div className="border border-border p-3 space-y-2" data-testid="sell-on-exchange-block">
              <div className="flex items-center justify-between">
                <span className="text-sm">Also sell on {EX_LABELS[sellEx.exchange]}</span>
                <Switch data-testid="sell-on-exchange-switch" checked={sellOnExchange}
                  onCheckedChange={(v) => { setSellOnExchange(v); if (!v) setSellConfirm(false); }} />
              </div>
              {sellOnExchange && (
                <div className="space-y-2">
                  <p className="text-xs text-loss">
                    Places a REAL market sell order for {fmt(trade.quantity)} {trade.symbol} on {EX_LABELS[sellEx.exchange]}. Real financial risk applies.
                  </p>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid="sell-confirm-checkbox" checked={sellConfirm}
                      onCheckedChange={(v) => setSellConfirm(!!v)} />
                    I understand and confirm this live sell order
                  </label>
                </div>
              )}
            </div>
          )}
          <Button data-testid="confirm-close-btn" onClick={closeTrade} disabled={!exitPrice}
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
            Close &amp; Compute P/L
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={pushOpen} onOpenChange={(o) => { setPushOpen(o); if (!o) setPushConfirm(false); }}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="push-order-dialog">
          <DialogHeader><DialogTitle className="font-heading text-loss">⚠ Live Order Confirmation</DialogTitle></DialogHeader>
          <div className="text-sm space-y-1 font-mono border border-border p-3">
            <div>Exchange: {exLabel} ({liveEx?.environment})</div>
            <div>Symbol: {trade.symbol}</div>
            <div>Side: {trade.side === "long" ? "Buy" : "Sell"} (Limit @ {fmt(trade.entry_price)})</div>
            <div>Qty: {fmt(trade.quantity)}</div>
            <div>SL: {trade.stop_loss != null ? fmt(trade.stop_loss) : "none"} · TP: {trade.take_profits?.[0]?.target_price != null ? fmt(trade.take_profits[0].target_price) : "none"}</div>
          </div>
          <p className="text-xs text-muted-foreground">This places a REAL order on the exchange. Real financial risk applies.</p>
          {trade.exchange_order_id && (
            <p className="text-xs text-loss" data-testid="duplicate-order-warning">
              An order was already placed for this trade (ID {trade.exchange_order_id}). This will place another one.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="push-confirm-checkbox" checked={pushConfirm} onCheckedChange={(v) => setPushConfirm(!!v)} />
            I understand and confirm this live order
          </label>
          <Button data-testid="push-submit-btn" disabled={!pushConfirm || pushing} onClick={pushToExchange}
            className="bg-[hsl(var(--loss))] text-white hover:bg-[hsl(var(--loss))] hover:brightness-90 hover:text-white">
            {pushing ? "Placing order…" : "Place Live Order"}
          </Button>
        </DialogContent>
      </Dialog>

      <TradeFormDialog open={editOpen} onOpenChange={setEditOpen} trade={trade} onSaved={load} />
      <PhotoCropper file={cropFile} onDone={handleCropDone} />
      <PhotoLightbox photos={trade.photos || []} index={lightboxIndex} onClose={() => setLightboxIndex(null)} />
    </div>
  );
}
